/**
 * Operation Mode — world regions for the Plague-style infiltration sim.
 * Reuses the 10 conquest countries, adding adjacency + environment traits.
 * Headless: no DOM, pure data.
 */

export type Climate = 'arid' | 'tropical' | 'temperate' | 'cold' | 'urban';

export interface RegionDef {
  id: string;
  nameKey: string;      // reuse existing conquest i18n keys (cq_*)
  /** Adjacent region ids — infiltration can bleed across these links. */
  adjacency: string[];
  /** Wealth 1-5: higher = faster human resistance research once exposed. */
  wealth: 1 | 2 | 3 | 4 | 5;
  /** Surveillance 1-5: higher = exposure rises faster (harder to stay hidden). */
  surveillance: 1 | 2 | 3 | 4 | 5;
  climate: Climate;
  /** Population weight 1-5: higher = more DNA generated while infiltrated. */
  population: 1 | 2 | 3 | 4 | 5;
  /** Whether the reptilian operation may begin here. */
  startable: boolean;
}

/**
 * 10 regions. Adjacency forms a rough world graph so infiltration spreads
 * continent-to-continent (air/sea/land vectors decide how fast).
 */
export const REGIONS: RegionDef[] = [
  {
    id: 'egypt', nameKey: 'cq_egypt', adjacency: ['india', 'uk', 'russia'],
    wealth: 2, surveillance: 2, climate: 'arid', population: 3, startable: true,
  },
  {
    id: 'brazil', nameKey: 'cq_brazil', adjacency: ['usa', 'uk'],
    wealth: 2, surveillance: 2, climate: 'tropical', population: 4, startable: true,
  },
  {
    id: 'india', nameKey: 'cq_india', adjacency: ['egypt', 'china', 'australia'],
    wealth: 2, surveillance: 3, climate: 'tropical', population: 5, startable: true,
  },
  {
    id: 'australia', nameKey: 'cq_australia', adjacency: ['india', 'japan'],
    wealth: 4, surveillance: 3, climate: 'arid', population: 2, startable: true,
  },
  {
    id: 'japan', nameKey: 'cq_japan', adjacency: ['korea', 'china', 'australia'],
    wealth: 5, surveillance: 4, climate: 'temperate', population: 3, startable: true,
  },
  {
    id: 'korea', nameKey: 'cq_korea', adjacency: ['japan', 'china'],
    wealth: 4, surveillance: 4, climate: 'temperate', population: 3, startable: true,
  },
  {
    id: 'uk', nameKey: 'cq_uk', adjacency: ['egypt', 'brazil', 'russia', 'usa'],
    wealth: 5, surveillance: 4, climate: 'temperate', population: 3, startable: true,
  },
  {
    id: 'russia', nameKey: 'cq_russia', adjacency: ['egypt', 'uk', 'china'],
    wealth: 3, surveillance: 3, climate: 'cold', population: 3, startable: true,
  },
  {
    id: 'china', nameKey: 'cq_china', adjacency: ['india', 'russia', 'japan', 'korea'],
    wealth: 4, surveillance: 5, climate: 'temperate', population: 5, startable: false,
  },
  {
    id: 'usa', nameKey: 'cq_usa', adjacency: ['uk', 'brazil'],
    wealth: 5, surveillance: 5, climate: 'urban', population: 4, startable: false,
  },
];

const REGION_MAP: Record<string, RegionDef> =
  Object.fromEntries(REGIONS.map((r) => [r.id, r]));

/** Look up a region definition by id. */
export function getRegion(id: string): RegionDef | undefined {
  return REGION_MAP[id];
}

/** All startable region ids (China/USA are locked as hardest final targets). */
export function startableRegionIds(): string[] {
  return REGIONS.filter((r) => r.startable).map((r) => r.id);
}
